import { create } from 'zustand';

import { IAgoraStreamingData, IAttendeeDetails, IShareScreenStreamCredentials, IStreamDetails } from '@src/types';

interface IWebinarRoomStore {
  webinarId?: string;
  setWebinarId: (id: string) => void;

  streamDetails?: IStreamDetails;

  agoraStreamingData?: IAgoraStreamingData;
  setAgoraStreamingData: (data: IAgoraStreamingData) => void;

  attendeeDetails?: IAttendeeDetails;
  setAttendeeDetails: (data: IAttendeeDetails) => void;

  isWebinarStartedByHost?: boolean;
  setIsWebinarStartedByHost: (value: boolean) => void;

  shareScreenStreamCredentials?: IShareScreenStreamCredentials;
  setShareScreenStreamCredentials: (data: IShareScreenStreamCredentials) => void;

  viewersPinnedPeerUids: number[];
  setViewersPinnedPeerUids: (ids: number[]) => void;
  toggleViewerPinForPeerUid: (peerStreamUid: number) => void;
  handleViewerIframeLayoutUpdate: (layout: string, pinUserIds?: number[], unpinUserIds?: number[]) => void;
  initializeViewerPinsFromServer: (pinedUserIds: number[]) => void;
  removeViewerPinnedPeerUids: (ids: number[]) => void;
}

const useWebinarRoomStore = create<IWebinarRoomStore>((set) => ({
  webinarId: '',
  setWebinarId: (webinarId) =>
    set((state) => {
      return { ...state, webinarId };
    }),

  streamDetails: {
    viewerHLS: '',
    viewerIframeSrc: '',
  },

  agoraStreamingData: {
    status: false,
    token: '',
    userId: '',
    stream_uid: 0,
  },

  setAgoraStreamingData: (agoraStreamingData) => set({ agoraStreamingData }),

  attendeeDetails: undefined,
  setAttendeeDetails: (attendeeDetails) =>
    set((state) => {
      return { ...state, attendeeDetails };
    }),

  isWebinarStartedByHost: false,
  setIsWebinarStartedByHost: (isWebinarStartedByHost) => set((state) => ({ ...state, isWebinarStartedByHost })),

  shareScreenStreamCredentials: {
    stream_uid: 0,
    token: '',
  },
  setShareScreenStreamCredentials: (shareScreenStreamCredentials) =>
    set((state) => ({ ...state, shareScreenStreamCredentials })),

  viewersPinnedPeerUids: [],
  setViewersPinnedPeerUids: (ids) =>
    set((state) => {
      return { ...state, viewersPinnedPeerUids: ids };
    }),
  toggleViewerPinForPeerUid: (peerStreamUid) =>
    set((state) => {
      const current = state.viewersPinnedPeerUids || [];
      const exists = current.includes(peerStreamUid);
      if (exists) {
        const next = current.filter((id) => id !== peerStreamUid);
        return { ...state, viewersPinnedPeerUids: next };
      } else {
        const next = [...current, peerStreamUid];
        return { ...state, viewersPinnedPeerUids: next };
      }
    }),
  handleViewerIframeLayoutUpdate: (layout, pinUserIds, unpinUserIds) =>
    set((state) => {
      if (layout === 'PIN' && pinUserIds) {
        return { ...state, viewersPinnedPeerUids: pinUserIds };
      } else if (layout === 'UNPIN' && unpinUserIds) {
        const updated = state.viewersPinnedPeerUids.filter((id) => !unpinUserIds.includes(id));
        return { ...state, viewersPinnedPeerUids: updated };
      }

      return state;
    }),
  initializeViewerPinsFromServer: (pinedUserIds) =>
    set((state) => {
      return { ...state, viewersPinnedPeerUids: pinedUserIds || [] };
    }),
  removeViewerPinnedPeerUids: (ids) =>
    set((state) => {
      if (ids.length === 0) {
        return state;
      }
      const idsSet = new Set(ids);
      const filteredPinnedUids = state.viewersPinnedPeerUids.filter((uid) => !idsSet.has(uid));

      if (filteredPinnedUids.length === state.viewersPinnedPeerUids.length) {
        return state;
      }

      return { ...state, viewersPinnedPeerUids: filteredPinnedUids };
    }),
}));

export default useWebinarRoomStore;
